import axios from 'axios';
import Select from '../../common/select';
import PoiMapsLoader from './poi-maps-loader';
import PoiMaps from './poi-maps';

import { debounce } from 'throttle-debounce';

export default class Poi {
    constructor() {
        this.$mapContainer = document.querySelector('.poi-map');
        this.$map = document.querySelector('.poi-map__google');
        this.$form = document.querySelector('.filter-block__form');
        this.$formErrors = document.querySelector('.filter-block__errors');
        this.$result = document.querySelector('.poi-map__result');
        this.$resultLoader = document.querySelector('.poi-map__result-loader');
        this.$openFrom = document.querySelector('#id_open_from');
        this.$openTo = document.querySelector('#id_open_to');
        this.mainCities = [ // list of all main cities for every voivodship
            'Wrocław',
            'Bydgoszcz',
            'Lublin',
            'Gorzów Wielkopolski',
            'Łódź',
            'Kraków',
            'Warszawa',
            'Opole',
            'Rzeszów',
            'Białystok',
            'Gdańsk',
            'Katowice',
            'Kielce',
            'Olsztyn',
            'Poznań',
            'Szczecin'
        ];

        if (!this.$mapContainer || !this.$map) {
            return;
        }

        this.state = {
            urlGetPois: location.pathname,
            urlGetAutocomplete: '/poi',
            currentBounds: null,
            isFirstRendered: false,
            isLoading: false, // true if any loading is on page
            formData: {}, // json with data from form
            resultData: [] // result from api
        }

        this.init();
    }

    init() {
        // reset form states after page reload
        this.$form.reset();
        this.$form.querySelector('[name="address"]').disabled = true;

        PoiMapsLoader.load().then((google) => {
            this.google = new PoiMaps(google);

            this.google.initMap(this.$map, {
                zoom: 6,
                center: new this.google.instance.LatLng(52.23612192261582, 19.370866133298794),
                clickableIcons: false,
                mapTypeControl: false,
                fullscreenControl: false,
                styles: [
                    {
                        featureType: 'poi',
                        elementType: 'labels',
                        stylers: [{ visibility: 'off' }]
                    }
                ]
            });

            this.eventFormSubmit();
            this.eventChangeVoivodship();
            this.eventChangeCity();
            this.eventMapIdle();
            this.appendCustomSelect();
        });
    }

    /**
     * convert form data into object
     */
    prepareFormData() {
        const formData = new FormData(this.$form);
        const result = {};

        formData.forEach((d, k) => {
            if (result[k] !== undefined) {
                result[k] += `,${d}`;
            } else {
                result[k] = d;
            }
        })

        return result;
    }

    /**
     * get lat and lng of selected city in form
     */
    getLatLngData() {
        const { type, service, address, ...rest } = this.state.formData;
        const data = {
            params: {
                get_coords: '',
                address: address || '',
                ...rest
            }
        };

        return axios.get(this.state.urlGetPois, data);
    }

    /**
     * get pois for visible map area
     * always run after getLatLngData()
     */
    getPoiData() {
        const { address, ...rest } = this.state.formData;
        const data = {
            params: {
                open_day: '',
                filter_atm_type: '',
                search: '',
                open_from: this.$openFrom.value.trim(),
                open_to: this.$openTo.value.trim(),
                address: address || '',
                ...rest
            }
        };
        const calls = this.state.currentBounds.full.map((bounds) => {
            return axios.get(this.state.urlGetPois, {
                ...data,
                params: {
                    ...data.params,
                    bounds
                }
            });
        });

        return Promise.all(calls);
    }

    /**
     * handle response from getPoiData()
     * render results below map + creating marker and adding to map
     */
    handlePoiResponses(re) {
        this.state.resultData = re.map((current) => {
            if (current.status === 200) {
                return current.data.result;
            }
        }).flat();

        // sort result by distance from map center
        this.state.resultData = this.google.sortDataByDistance(this.state.resultData, this.google.map.getCenter());

        if (this.state.resultData.length === 0) { // hide result if there is no any result
            this.toggleFilterLoader(false);
            this.toggleContentLoader(false);
            this.$result.style.display = 'none';
            this.toggleFormErros('Brak placówek dla podanych kryteriów');
            return;
        }

        if (!this.state.isFirstRendered) { // set to true after first render result
            this.state.isFirstRendered = true;
        }

        this.renderResultList(this.state.resultData, true);

        this.toggleFilterLoader(false);
        this.toggleContentLoader(false);
    }

    /**
     * render result html list + add marker (if needed)
     * @param data - array of elements
     * @param alsoAddMarker - if true than add marker to map
     */
    renderResultList(data, alsoAddMarker = true) {
        let result = '';

        if (alsoAddMarker) { // clear previouse added markers before create new ones
            this.google.clearMarkers();
        }

        data.forEach((item) => {
            const {name, contact, address, opening_hours} = item;

            if (alsoAddMarker) {
                this.google.createMarker(item);
            }

            result += `
                <tr>
                    <td>${name}</td>
                    <td>${contact}</td>
                    <td>${address}</td>
                    <td>${opening_hours || '-'}</td>
                </tr>
            `;
        });

        if (alsoAddMarker) {
            this.google.addMarkersToMap();
        }

        this.$result.style.display = 'block';
        this.$result.querySelector('tbody').innerHTML = result;
    }

    toggleFormErros(errorString = '') {
        if (errorString.length === 0) {
            this.$formErrors.style.display = 'none';
        } else {
            this.$formErrors.style.display = 'block';
        }

        this.$formErrors.innerHTML = errorString;
    }

    toggleFilterLoader(state) {
        this.state.isLoading = state;
        this.$form.closest('.filter-block').classList.toggle('filter-block--loading', state);

        if (state) {
            this.$form.closest('.filter-block').insertAdjacentHTML('beforeend', '<div class="loader"></div>')
        } else {
            this.$form.closest('.filter-block').querySelector('.loader').remove();
        }
    }

    toggleContentLoader(state) {
        this.state.isLoading = state;

        if (state) {
            this.$resultLoader.style.display = 'block';
        } else {
            this.$resultLoader.style.display = 'none';
        }
    }

    appendCustomSelect() {
        const $select = this.$form.querySelector('[name="voivodship"]');

        new Select($select);
    }

    /**
     * submit filter form
     */
    eventFormSubmit() {
        this.$form.addEventListener('submit', (e) => {
            e.preventDefault();

            this.toggleFormErros('');
            this.toggleFilterLoader(true);

            this.state.formData = this.prepareFormData();

            // user must check at least one checkbox
            if (this.state.formData.type === undefined) {
                this.toggleFilterLoader(false);
                this.toggleFormErros('Aby wyszukać musisz rozszerzyć kryteria wyszukiwania');
                return;
            }

            const city = this.state.formData.city;
            const address = this.state.formData.address || '';
            let zoom = 13;

            if(address === '') {
              if(city === 'Wrocław') zoom = 11.5;
              if(city === 'Bydgoszcz') zoom = 11.5;
              if(city === 'Lublin') zoom = 12;
              if(city === 'Gorzów Wielkopolski') zoom = 13;
              if(city === 'Łódź') zoom = 11.5;
              if(city === 'Kraków') zoom = 12;
              if(city === 'Warszawa') zoom = 11;
              if(city === 'Opole') zoom = 13;
              if(city === 'Rzeszów') zoom = 13;
              if(city === 'Białystok') zoom = 12.5;
              if(city === 'Gdańsk') zoom = 11;
              if(city === 'Katowice') zoom = 12;
              if(city === 'Kielce') zoom = 12.5;
              if(city === 'Olsztyn') zoom = 12;
              if(city === 'Poznań') zoom = 11;
              if(city === 'Szczecin') zoom = 12;
            }

            this.getLatLngData().then(({data}) => {
                const { lat, lng } = data.result;

                if (!lat || !lng) {
                    this.toggleFilterLoader(false);
                    this.toggleFormErros('Brak punktów sprzedaży w podanej lokalizacji');
                    return;
                }

                this.toggleContentLoader(true);

                this.google.map.setCenter(new this.google.instance.LatLng(lat, lng));
                this.google.map.setZoom(zoom);

                this.state.currentBounds = this.google.getBounds();

                this.getPoiData()
                    .then(this.handlePoiResponses.bind(this))
                    .catch((e) => {
                        this.toggleFilterLoader(false);
                        this.toggleFormErros('Brak możliwości pobrania listy placówek');
                    })
            }).catch((e) => {
                this.toggleFilterLoader(false);
                this.toggleFormErros('Błąd przy pobieraniu koordynatów dla podanych kryteriów');
            });
        });
    }

    /**
     * fill field with default city after change voivodship
     */
    eventChangeVoivodship() {
        this.$form.querySelector('[name="voivodship"]').addEventListener('change', (e) => {
            const $input = this.$form.querySelector('[name="city"]');

            $input.value = this.mainCities[e.target.value - 1] || '';
            $input.dispatchEvent(new Event('input', {bubbles:true}));
        });
    }

    /**
     * event for changing city value
     */
    eventChangeCity() {
        this.$form.querySelector('[name="city"]').addEventListener('input', (e) => {
            const value = e.target.value.trim();
            const $address = this.$form.querySelector('[name="address"]')

            $address.disabled = value.length <= 0;
        });
    }

    /**
     * event for map moving (user interact with map)
     */
    eventMapIdle() {
        this.google.instance.event.addListener(this.google.map, 'idle', debounce(600, () => {
            if (this.state.isFirstRendered && this.state.currentBounds !== null && !this.state.isLoading) {
                const filteredData = this.state.resultData.filter(({lat, lng}) => this.google.checkIfLatLngIsOnMap(lat, lng));

                // rerender only table below map without change markers
                this.renderResultList(filteredData, false);
            }
        }));
    }
}


// WEBPACK FOOTER //
// ./front/treasurybond/js/components/poi/poi.js